<template>
  <div class="display-root">
    <!-- Grid texture background -->
    <div class="grid-texture"></div>

    <!-- ══ HEADER ══ -->
    <header class="display-header">
      <div class="header-brand">
        <img src="http://cdn.bps.go.id/bps.png" class="header-logo" />
        <div class="header-titles">
          <div class="header-title">BADAN PUSAT STATISTIK</div>
          <div class="header-title">PROVINSI KALIMANTAN TENGAH</div>
        </div>
      </div>
    </header>

    <!-- ══ MAIN ══ -->
    <main class="display-main">
      <!-- ─── ACTIVE: Petugas on duty ─── -->
      <div v-if="activeCard" class="active-layout">
        <!-- LEFT PANEL -->
        <div class="active-left">
          <div class="badge-row">
            <span class="shift-badge">Petugas Pelayanan</span>
          </div>

          <h1 class="hero-name">
            <span
              v-for="(line, i) in splitName(activeCard.name)"
              :key="i"
              class="hero-name-line"
              >{{ line }}</span
            >
          </h1>

          <div class="hero-divider">
            <div class="hero-divider-accent"></div>
            <div class="hero-divider-line"></div>
          </div>

          <div class="shift-time-info">
            <span class="shift-time-label">Jam Pelayanan</span>
            <span class="shift-time-value">{{
              activeCard.shift_label || "08:00 – 16:00"
            }}</span>
          </div>

          <div
            class="carousel-outer"
            @mouseenter="pauseCarousel"
            @mouseleave="resumeCarousel"
          >
            <button
              class="carousel-nav prev"
              @click="carouselMove(-1)"
              :disabled="carouselIndex === 0"
              aria-label="Sebelumnya"
            >
              <span>&#8249;</span>
            </button>
            <button
              class="carousel-nav next"
              @click="carouselMove(1)"
              :disabled="carouselIndex === (weeklySchedules || []).length - 1"
              aria-label="Berikutnya"
            >
              <span>&#8250;</span>
            </button>

            <div class="carousel-track-wrap" ref="trackWrap">
              <div
                class="carousel-track"
                :style="{ transform: `translateX(${carouselOffset}px)` }"
              >
                <div
                  v-for="(day, idx) in weeklySchedules || []"
                  :key="day.date"
                  class="weekly-day"
                  :class="{
                    'weekly-day--today': day.isToday,
                    'weekly-day--active': idx === carouselIndex,
                  }"
                  :style="{
                    opacity:
                      idx === carouselIndex
                        ? 1
                        : idx === carouselIndex + 1
                        ? 0.55
                        : 0.35,
                  }"
                  @click="carouselGoTo(idx)"
                >
                  <div
                    class="weekly-day-header"
                    :class="{ 'weekly-day-header--today': day.isToday }"
                  >
                    <div class="dh-top">
                      <span
                        class="w-day-name"
                        :class="{ 'w-day-name--today': day.isToday }"
                        >{{ day.dayName }}</span
                      >
                      <span
                        class="w-day-date"
                        :class="{ 'w-day-date--today': day.isToday }"
                      >
                        {{
                          day.isToday
                            ? (day.dateDisplay || "").split(" ")[0]
                            : day.dateDisplay || ""
                        }}
                      </span>
                    </div>
                    <span v-if="day.isToday" class="today-badge">Hari ini</span>
                  </div>
                  <div class="weekly-day-content">
                    <div
                      v-if="day.petugas && day.petugas.length > 0"
                      class="w-petugas-list"
                    >
                      <div v-for="(p, pidx) in day.petugas" :key="p.id">
                        <div
                          v-if="pidx > 0 && day.isToday"
                          class="w-divider"
                        ></div>
                        <div class="w-petugas-item">
                          <div
                            class="w-avatar"
                            :class="{
                              'w-avatar--today': day.isToday,
                              'w-avatar--active': day.isToday && pidx === 0,
                            }"
                          >
                            <img
                              v-if="getPetugasPhoto(p)"
                              :src="getPetugasPhoto(p)"
                              style="
                                width: 100%;
                                height: 100%;
                                object-fit: cover;
                                object-position: top center;
                              "
                            />
                            <span v-else>{{ (p.name || "?").charAt(0) }}</span>
                          </div>
                          <div class="w-info">
                            <span
                              class="w-name"
                              :class="{ 'w-name--today': day.isToday }"
                              >{{ formatShortName(p.name) }}</span
                            >
                            <span
                              class="w-shift"
                              :class="[
                                (p.shift_type || '').toLowerCase(),
                                { 'w-shift--today': day.isToday },
                              ]"
                              >{{ p.shift_type }}</span
                            >
                          </div>
                        </div>
                      </div>
                    </div>
                    <div
                      v-else
                      class="w-empty"
                      :class="{ 'w-empty--today': day.isToday }"
                    >
                      <span>—</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div class="carousel-dots">
              <span
                v-for="(day, idx) in weeklySchedules || []"
                :key="'dot' + idx"
                class="carousel-dot"
                :class="{ active: idx === carouselIndex }"
                @click="carouselGoTo(idx)"
              ></span>
            </div>
          </div>
        </div>

        <!-- RIGHT PANEL -->
        <div class="active-right">
          <!-- Yellow half-circle background -->
          <div class="yellow-circle"></div>

          <!-- Dot grid — upper left -->
          <div class="dot-grid dot-grid-tl">
            <span v-for="i in 25" :key="'dtl' + i" class="dot"></span>
          </div>
          <!-- Dot grid — lower right -->
          <div class="dot-grid dot-grid-br">
            <span v-for="i in 16" :key="'dbr' + i" class="dot"></span>
          </div>

          <!-- Star decorations -->
          <span class="star star-1">✦</span>
          <span class="star star-2">✦</span>
          <span class="star star-3">✦</span>
          <span class="star star-4">●</span>

          <!-- Photo frame -->
          <div class="photo-frame">
            <img
              v-if="activeCard.photo"
              :src="activeCard.photo"
              :alt="activeCard.name"
              class="photo-img"
            />
            <div v-else class="photo-placeholder">
              <span class="photo-initial">{{
                (activeCard.name || "?").charAt(0).toUpperCase()
              }}</span>
            </div>
          </div>
        </div>
      </div>

      <!-- ─── STANDBY: No petugas on duty ─── -->
      <div v-else class="standby-layout">
        <div class="standby-rings">
          <div class="standby-ring standby-ring-outer"></div>
          <div class="standby-ring standby-ring-inner"></div>
          <span class="standby-icon">🏢</span>
        </div>
        <div class="standby-text">
          <p class="standby-title">
            {{ isWorkday ? "Di Luar Jam Kerja" : "Hari Libur" }}
          </p>
          <p class="standby-sub">
            Jadwal tersedia Senin – Jumat &nbsp;·&nbsp; 08:00 – 16:30
          </p>
        </div>
      </div>
    </main>

    <!-- ══ QUEUE ANNOUNCEMENT OVERLAY ══ -->
    <transition name="queue-fade">
      <div v-if="showQueueOverlay" class="queue-overlay">
        <div class="queue-overlay-card">
          <p class="queue-overlay-label">Nomor Antrian</p>
          <p class="queue-overlay-number">{{ noAntrian }}</p>
          <p class="queue-overlay-sub">Silakan menuju loket</p>
        </div>
      </div>
    </transition>

    <!-- Preview mode indicator -->
    <div v-if="previewMode" class="preview-indicator">
      <span>⚡ PREVIEW MODE</span>
    </div>
  </div>
</template>

<script>
import axios from "axios";
import Echo from "laravel-echo";
import audioFile from "@/assets/tingtung.mp3";

axios.defaults.headers.common["X-Requested-With"] = "XMLHttpRequest";
axios.defaults.headers.common["Content-Type"] = "application/json";
window.Pusher = require("pusher-js");
window.Echo = new Echo({
  broadcaster: "pusher",
  key: "72b390d6913fa906ab84",
  cluster: "ap1",
  forceTLS: true,
  encrypted: false,
});

export default {
  name: "Dashboard",
  data() {
    return {
      activeCard: null,
      noAntrian: "",
      showQueueOverlay: false,
      audio: null,
      clockStr: "",
      clockInterval: null,
      previewMode: false,
      isWorkday: true,
      weeklySchedules: [],
      carouselIndex: 0,
      carouselOffset: 0,
      carouselTimer: null,
    };
  },

  created() {
    // Check for preview mode from URL query
    var urlParams = new URLSearchParams(window.location.search);
    this.previewMode = urlParams.get("previewMode") === "active";

    this.checkWorkday();
    this.fetchCurrentDuty();
    this.loadQueueData();
  },

  mounted() {
    this.audio = new Audio(audioFile);

    // Clock
    this.updateClock();
    this.clockInterval = setInterval(this.updateClock, 1000);

    // Pusher: listen for queue announcements
    window.Echo.channel("queue-updated-channel").listen(
      ".queue-updated",
      (event) => {
        this.loadQueueData();
        this.showQueueAnnouncement(event);
      }
    );

    // Auto-refresh every 5 minutes to pick up schedule changes
    setInterval(() => {
      this.fetchCurrentDuty();
    }, 5 * 60 * 1000);

    this.initCarousel();
  },

  beforeDestroy() {
    if (this.clockInterval) clearInterval(this.clockInterval);
    this.pauseCarousel();
  },

  methods: {
    checkWorkday() {
      var day = new Date().getDay();
      this.isWorkday = day >= 1 && day <= 5; // Mon-Fri

      // Also check API holidays
      var today = new Date();
      var month = today.getMonth() + 1;
      var year = today.getFullYear();
      axios
        .get("holidays?month=" + month + "&year=" + year)
        .then(
          function (res) {
            var todayStr = today.toISOString().substring(0, 10);
            var isHoliday = res.data.some(function (h) {
              return h.date.substring(0, 10) === todayStr;
            });
            if (isHoliday) {
              this.isWorkday = false;
            }
          }.bind(this)
        )
        .catch(function (err) {
          console.error(err);
        });
    },

    updateClock() {
      var d = new Date();
      var h = String(d.getHours()).padStart(2, "0");
      var m = String(d.getMinutes()).padStart(2, "0");
      var s = String(d.getSeconds()).padStart(2, "0");
      this.clockStr = h + ":" + m + ":" + s;
    },

    fetchCurrentDuty() {
      var url = this.previewMode
        ? "schedules/current?preview=true"
        : "schedules/current";

      axios
        .get(url)
        .then((response) => {
          const res = response.data;
          if (!res) return;

          // The API returns a status object with an on_duty array, or sometimes just an array
          const onDuty = res.on_duty || (Array.isArray(res) ? res : []);

          // Update weekly schedule if available in the response
          if (res.weekly_schedules && Array.isArray(res.weekly_schedules)) {
            this.weeklySchedules = res.weekly_schedules.map((w) => ({
              date: w.date,
              dayName: w.day_name,
              dateDisplay: w.date_display,
              isToday: w.is_today,
              petugas: Array.isArray(w.petugas) ? w.petugas : [],
            }));
            this.$nextTick(() => {
              this.initCarousel();
            });
          } else {
            this.weeklySchedules = [];
          }

          // Update workday status from backend if available
          if (res.is_working_hours !== undefined) {
            this.isWorkday = !res.is_weekend && !res.is_holiday;
          }

          if (response.status === 200 && onDuty.length > 0) {
            var petugas = onDuty[0];
            var photo = this.getPetugasPhoto(petugas);

            this.activeCard = {
              name: petugas.name || "Petugas",
              photo: photo,
              shift_label: petugas.shift_label || "08:00 – 16:00",
            };
          } else {
            this.activeCard = null;
          }
        })
        .catch((error) => {
          console.error("Error fetching current duty:", error);
          this.activeCard = null;
        });
    },

    loadQueueData() {
      axios
        .get("getTodayQueue")
        .then((response) => {
          if (response.status === 200) {
            this.noAntrian =
              response.data
                .filter((obj) => obj.is_call === "1")
                .sort(
                  (a, b) => new Date(b.updated_at) - new Date(a.updated_at)
                )[0]?.noantrian || "-";
          }
        })
        .catch((error) => {
          console.log(error);
        });
    },

    showQueueAnnouncement(event) {
      this.showQueueOverlay = true;

      this.playSequence()
        .then(() => {
          var textSpeech = "Nomor Antrian ";
          var speech = new SpeechSynthesisUtterance(textSpeech);
          speech.lang = "id-ID";
          speech.rate = 0.95;
          speech.voice = speechSynthesis
            .getVoices()
            .find((voice) => voice.lang === "id-ID");
          window.speechSynthesis.speak(speech);

          var speechAntrian = new SpeechSynthesisUtterance(
            event.queue.noantrian
          );
          speechAntrian.lang = "id-ID";
          speechAntrian.rate = 0.8;
          speechAntrian.voice = speechSynthesis
            .getVoices()
            .find((voice) => voice.lang === "id-ID");
          window.speechSynthesis.speak(speechAntrian);

          var finalSpeech = "Silakan menuju loket. Terima kasih";
          var speechFinal = new SpeechSynthesisUtterance(finalSpeech);
          speechFinal.lang = "id-ID";
          speechFinal.rate = 0.95;
          speechFinal.voice = speechSynthesis
            .getVoices()
            .find((voice) => voice.lang === "id-ID");
          window.speechSynthesis.speak(speechFinal);

          speechFinal.onend = () => {
            setTimeout(() => {
              this.showQueueOverlay = false;
            }, 2000);
          };
        })
        .catch((error) => {
          console.error("Error playing audio:", error);
          setTimeout(() => {
            this.showQueueOverlay = false;
          }, 8000);
        });
    },

    playAudio() {
      return new Promise((resolve, reject) => {
        if (!this.audio) {
          this.audio = new Audio(audioFile);
        }
        this.audio.onended = () => {
          resolve();
        };
        this.audio.onerror = (e) => {
          reject(e);
        };
        this.audio.play();
      });
    },

    async playSequence() {
      try {
        await this.playAudio();
      } catch (error) {
        console.error("Error playing audio:", error);
      }
    },

    splitName(name) {
      if (!name) return [""];
      var words = name.trim().split(/\s+/);
      if (words.length === 1) return [words[0]];
      var mid = Math.ceil(words.length / 2);
      return [words.slice(0, mid).join(" "), words.slice(mid).join(" ")];
    },

    getCurrentWeekDates() {
      const curr = new Date();
      const day = curr.getDay();
      const diff = curr.getDate() - day + (day === 0 ? -6 : 1); // Adjust to Monday
      const monday = new Date(curr.setDate(diff));

      const week = [];
      for (let i = 0; i < 5; i++) {
        // Mon to Fri
        const d = new Date(monday);
        d.setDate(monday.getDate() + i);
        week.push(d);
      }
      return week;
    },

    getPetugasPhoto(petugas) {
      if (!petugas) return null;
      if (petugas.fotoPelayan) {
        var base = axios.defaults.baseURL.replace(/\/api\/?$/, "/");
        return base + petugas.fotoPelayan;
      }
      return petugas.img_url || null;
    },

    formatShortName(name) {
      if (!name) return "";
      const words = name.split(" ");
      if (words.length <= 2) return name;
      // Return first 2 words
      return words.slice(0, 2).join(" ");
    },

    // ══ CAROUSEL METHODS ══
    initCarousel() {
      const todayIdx = this.weeklySchedules.findIndex((d) => d.isToday);
      this.carouselIndex = todayIdx >= 0 ? todayIdx : 0;
      this.$nextTick(() => {
        this.updateCarouselOffset();
        this.resumeCarousel();
      });
    },

    updateCarouselOffset() {
      const wrap = this.$refs.trackWrap;
      if (!wrap) return;
      const wrapW = wrap.offsetWidth;
      const GAP = 10;
      const PEEK = 50;
      const todayW = 180;
      const normalW = 160;

      let offset = 0;
      for (let i = 0; i < this.carouselIndex; i++) {
        offset +=
          (i === 0 && this.weeklySchedules[i]?.isToday ? todayW : normalW) +
          GAP;
      }
      const curW = this.weeklySchedules[this.carouselIndex]?.isToday
        ? todayW
        : normalW;
      const center = (wrapW - curW - PEEK) / 2;
      this.carouselOffset = -(offset - center);
    },

    carouselMove(dir) {
      const max = this.weeklySchedules.length - 1;
      this.carouselIndex = Math.max(0, Math.min(max, this.carouselIndex + dir));
      this.updateCarouselOffset();
      this.resetCarouselAuto();
    },

    carouselGoTo(idx) {
      this.carouselIndex = idx;
      this.updateCarouselOffset();
      this.resetCarouselAuto();
    },

    resetCarouselAuto() {
      this.pauseCarousel();
      this.carouselTimer = setInterval(() => {
        const max = this.weeklySchedules.length - 1;
        this.carouselIndex =
          this.carouselIndex < max ? this.carouselIndex + 1 : 0;
        this.updateCarouselOffset();
      }, 3000);
    },

    pauseCarousel() {
      if (this.carouselTimer) {
        clearInterval(this.carouselTimer);
        this.carouselTimer = null;
      }
    },

    resumeCarousel() {
      this.resetCarouselAuto();
    },
  },
};
</script>

<style scoped>
@import url("https://fonts.googleapis.com/css2?family=Anton&display=swap");

/* ══════════════════════════════════════════════
   ROOT
   ══════════════════════════════════════════════ */
.display-root {
  position: relative;
  min-height: 100vh;
  overflow: hidden;
  background: #eef2f7;
}

/* Grid texture */
.grid-texture {
  position: absolute;
  inset: 0;
  z-index: 0;
  pointer-events: none;
  background-image: linear-gradient(rgba(26, 48, 96, 0.07) 1px, transparent 1px),
    linear-gradient(90deg, rgba(26, 48, 96, 0.07) 1px, transparent 1px);
  background-size: 44px 44px;
}

/* ══════════════════════════════════════════════
   HEADER
   ══════════════════════════════════════════════ */
.display-header {
  position: relative;
  z-index: 10;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px 48px;
}
.header-brand {
  display: flex;
  align-items: center;
  gap: 12px;
}
.header-logo {
  height: 48px;
}
.header-titles {
  display: flex;
  flex-direction: column;
  justify-content: center;
}
.header-title {
  color: #508da9;
  font-size: 14px;
  font-family: Arial, sans-serif;
  font-weight: bold;
  font-style: italic;
}

/* ══════════════════════════════════════════════
   MAIN
   ══════════════════════════════════════════════ */
.display-main {
  position: relative;
  z-index: 10;
  min-height: calc(100vh - 112px);
}

/* ── ACTIVE LAYOUT ────────────────────────── */
.active-layout {
  display: flex;
  min-height: calc(100vh - 112px);
  width: 100%;
  align-items: stretch;
  overflow: hidden;
}

/* Left panel */
.active-left {
  position: relative;
  z-index: 10;
  width: 50%;
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding: 32px 32px 32px 200px;
}

.badge-row {
  margin-bottom: 32px;
  display: flex;
  align-items: center;
  gap: 16px;
}

.shift-badge {
  display: inline-block;
  background: #1a3060;
  color: white;
  padding: 10px 24px;
  border-radius: 8px;
  font-size: 0.85em;
  font-weight: 900;
  letter-spacing: 0.22em;
  text-transform: uppercase;
}

.hero-name {
  margin: 0 0 32px 0;
  padding: 0;
  line-height: 0.88;
  color: #1a3060;
  font-family: "Anton", sans-serif;
  font-size: clamp(48px, 12vw, 96px);
  letter-spacing: -0.01em;
  text-transform: uppercase;
  word-break: break-word;
}
.hero-name-line {
  display: block;
}

.hero-divider {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 32px;
}
.hero-divider-accent {
  width: 56px;
  height: 3px;
  border-radius: 99px;
  background: #f5c800;
}
.hero-divider-line {
  flex: 1;
  height: 1px;
  background: rgba(26, 48, 96, 0.1);
}

.shift-time-info {
  display: flex;
  flex-direction: column;
  gap: 4px;
}
.shift-time-label {
  font-size: 0.75em;
  font-weight: 900;
  letter-spacing: 0.2em;
  text-transform: uppercase;
  color: rgba(26, 48, 96, 0.35);
}
.shift-time-value {
  font-family: "Courier New", monospace;
  font-size: 2em;
  font-weight: 900;
  color: rgba(26, 48, 96, 0.5);
  font-variant-numeric: tabular-nums;
}

/* Right panel */
.active-right {
  position: relative;
  flex: 1;
  display: flex;
  align-items: flex-end;
  justify-content: center;
  overflow: hidden;
  padding-right: 48px;
}

.yellow-circle {
  position: absolute;
  right: 32px;
  bottom: 0;
  z-index: 0;
  width: min(72%, 620px);
  aspect-ratio: 1;
  border-radius: 50% 50% 0 50%;
  background: #f5c800;
}

.photo-frame {
  position: relative;
  z-index: 3;
  width: min(70%, 480px);
  aspect-ratio: 3 / 4;
  overflow: hidden;
}
.photo-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: top;
}
.photo-placeholder {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: flex-end;
  justify-content: center;
  padding-bottom: 24px;
  background: transparent;
}
.photo-initial {
  font-family: "Anton", sans-serif;
  font-size: clamp(80px, 14vw, 180px);
  color: rgba(26, 48, 96, 0.2);
  line-height: 1;
  user-select: none;
}

/* Dot grids */
.dot-grid {
  position: absolute;
  z-index: 1;
  display: grid;
  gap: 7px;
}
.dot-grid-tl {
  top: 22%;
  left: 8%;
  grid-template-columns: repeat(5, 7px);
}
.dot-grid-br {
  right: 2%;
  bottom: 16%;
  grid-template-columns: repeat(4, 7px);
}
.dot {
  display: block;
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: rgba(26, 48, 96, 0.25);
}

/* Star decorations */
.star {
  position: absolute;
  z-index: 1;
  font-style: normal;
  line-height: 1;
}
.star-1 {
  top: 14%;
  left: 16%;
  font-size: 8em;
  color: #f5c800;
}
.star-2 {
  top: 10%;
  right: 6%;
  font-size: 3em;
  color: #ef4444;
}
.star-3 {
  bottom: 24%;
  left: 22%;
  font-size: 1.1em;
  color: #22c55e;
}
.star-4 {
  bottom: 10%;
  left: 6%;
  font-size: 1em;
  color: rgba(26, 48, 96, 0.3);
}

/* ── STANDBY LAYOUT ───────────────────────── */
.standby-layout {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: calc(100vh - 112px);
  gap: 32px;
  text-align: center;
}

.standby-rings {
  position: relative;
  width: 224px;
  height: 224px;
  display: flex;
  align-items: center;
  justify-content: center;
}
.standby-ring {
  position: absolute;
  border-radius: 50%;
}
.standby-ring-outer {
  inset: 0;
  background: rgba(26, 48, 96, 0.05);
  border: 1px solid rgba(26, 48, 96, 0.1);
}
.standby-ring-inner {
  inset: 32px;
  background: rgba(245, 200, 0, 0.2);
  border: 1px solid rgba(245, 200, 0, 0.4);
}
.standby-icon {
  position: relative;
  font-size: 4.5em;
}

.standby-text {
  display: flex;
  flex-direction: column;
  gap: 12px;
}
.standby-title {
  margin: 0;
  color: rgba(26, 48, 96, 0.3);
  font-family: "Anton", sans-serif;
  font-size: clamp(28px, 5vw, 56px);
  text-transform: uppercase;
  letter-spacing: -0.01em;
}
.standby-sub {
  margin: 0;
  font-size: 0.9em;
  font-weight: 600;
  letter-spacing: 0.15em;
  text-transform: uppercase;
  color: rgba(26, 48, 96, 0.3);
}

/* ══════════════════════════════════════════════
   QUEUE ANNOUNCEMENT OVERLAY
   ══════════════════════════════════════════════ */
.queue-overlay {
  position: fixed;
  inset: 0;
  z-index: 100;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(26, 48, 96, 0.85);
  backdrop-filter: blur(12px);
}
.queue-overlay-card {
  text-align: center;
  padding: 60px 100px;
  background: white;
  border-radius: 32px;
  box-shadow: 0 40px 80px rgba(0, 0, 0, 0.3);
}
.queue-overlay-label {
  margin: 0 0 8px 0;
  font-size: 1.5em;
  font-weight: 900;
  letter-spacing: 0.2em;
  text-transform: uppercase;
  color: rgba(26, 48, 96, 0.5);
}
.queue-overlay-number {
  margin: 0 0 16px 0;
  font-family: "Anton", sans-serif;
  font-size: clamp(80px, 15vw, 200px);
  line-height: 1;
  color: #1a3060;
  letter-spacing: 0.05em;
}
.queue-overlay-sub {
  margin: 0;
  font-size: 1.4em;
  font-weight: 700;
  color: rgba(26, 48, 96, 0.4);
  text-transform: uppercase;
  letter-spacing: 0.15em;
}

/* Queue overlay transitions */
.queue-fade-enter-active,
.queue-fade-leave-active {
  transition: opacity 0.5s ease;
}
.queue-fade-enter,
.queue-fade-leave-to {
  opacity: 0;
}

/* ══════════════════════════════════════════════
   PREVIEW MODE INDICATOR
   ══════════════════════════════════════════════ */
.preview-indicator {
  position: fixed;
  top: 16px;
  right: 16px;
  z-index: 200;
  background: #ef4444;
  color: white;
  padding: 8px 20px;
  border-radius: 8px;
  font-size: 0.8em;
  font-weight: 900;
  letter-spacing: 0.15em;
  box-shadow: 0 4px 12px rgba(239, 68, 68, 0.4);
}

.weekly-scroll-wrap {
  overflow-x: auto;
  overflow-y: visible;
  padding-bottom: 20px;
  margin-top: 8px;
  scrollbar-width: thin;
  scrollbar-color: rgba(26, 48, 96, 0.15) transparent;
  -webkit-overflow-scrolling: touch;
}

.weekly-scroll-wrap::-webkit-scrollbar {
  height: 4px;
}
.weekly-scroll-wrap::-webkit-scrollbar-track {
  background: transparent;
}
.weekly-scroll-wrap::-webkit-scrollbar-thumb {
  background: rgba(26, 48, 96, 0.15);
  border-radius: 99px;
}

.weekly-container {
  display: grid;
  grid-template-columns: repeat(5, minmax(140px, 1fr));
  gap: 8px;
  align-items: end;
  padding: 12px;
  border-radius: 18px;
  min-width: 740px;
}

/* ── CAROUSEL STYLES ──────────────────────── */
.carousel-outer {
  position: relative;
  border-radius: 18px;
  padding: 32px 0; /* Increased to avoid clipping */
  margin-top: 32px;
  overflow: hidden;
}

.carousel-track-wrap {
  overflow: visible; /* Allow the lift to show */
  padding: 24px 0;
}

.carousel-track {
  display: flex;
  gap: 10px;
  align-items: flex-end;
  transition: transform 0.5s cubic-bezier(0.4, 0, 0.2, 1);
  will-change: transform;
}

.weekly-day {
  flex: 0 0 160px;
  transition: opacity 0.4s ease, transform 0.25s ease, box-shadow 0.25s ease;
}

.weekly-day--today {
  flex: 0 0 180px;
}

.carousel-nav {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: #fff;
  border: 0.5px solid rgba(26, 48, 96, 0.12);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  z-index: 10;
  box-shadow: 0 2px 8px rgba(26, 48, 96, 0.1);
  color: #1a3060;
  font-size: 22px;
  line-height: 1;
  transition: all 0.2s;
}

.carousel-nav:hover {
  background: #1a3060;
  color: #fff;
  border-color: #1a3060;
}
.carousel-nav:disabled {
  opacity: 0.3;
  cursor: not-allowed;
}
.carousel-nav.prev {
  left: 6px;
}
.carousel-nav.next {
  right: 6px;
}

.carousel-dots {
  display: flex;
  justify-content: center;
  gap: 6px;
  margin-top: 4px;
}

.carousel-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: rgba(26, 48, 96, 0.15);
  cursor: pointer;
  transition: all 0.3s ease;
}

.carousel-dot.active {
  background: #1a3060;
  width: 18px;
  border-radius: 99px;
}

.weekly-day--active:not(.weekly-day--today) {
  background: #fff;
  border-color: rgba(26, 48, 96, 0.15);
  transform: translateY(-8px) scale(1.02);
  box-shadow: 0 10px 28px rgba(26, 48, 96, 0.13);
}

.weekly-day--today {
  transform: translateY(-14px) scale(1.04);
  box-shadow: 0 16px 40px rgba(26, 48, 96, 0.28),
    0 4px 12px rgba(26, 48, 96, 0.18);
}

.weekly-day--today.weekly-day--active {
  transform: translateY(-18px) scale(1.07);
  box-shadow: 0 24px 56px rgba(26, 48, 96, 0.35),
    0 6px 16px rgba(26, 48, 96, 0.22);
}

.weekly-day {
  display: flex;
  flex-direction: column;
  gap: 7px;
  padding: 10px 10px 12px;
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.3);
  border: 0.5px solid rgba(26, 48, 96, 0.07);
  transition: transform 0.25s ease, box-shadow 0.25s ease, background 0.25s ease,
    opacity 0.4s ease;
}

/* State: Normal card, centered (Active) */
.weekly-day--active:not(.weekly-day--today) {
  background: #fff;
  border-color: rgba(26, 48, 96, 0.15);
  transform: translateY(-8px) scale(1.02);
  box-shadow: 0 10px 28px rgba(26, 48, 96, 0.13);
}

/* State: Today card, not centered */
.weekly-day--today {
  background: #1a3060;
  border: none;
  border-radius: 16px;
  padding: 16px 14px 18px;
  position: relative;
  z-index: 2;
  transform: translateY(-14px) scale(1.04);
  box-shadow: 0 16px 40px rgba(26, 48, 96, 0.28),
    0 4px 12px rgba(26, 48, 96, 0.18);
}

/* State: Today card AND centered (Active Today) */
.weekly-day--today.weekly-day--active {
  transform: translateY(-18px) scale(1.07);
  box-shadow: 0 24px 56px rgba(26, 48, 96, 0.35),
    0 6px 16px rgba(26, 48, 96, 0.22);
}

.weekly-day-header {
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding-bottom: 8px;
  border-bottom: 0.5px solid rgba(26, 48, 96, 0.1);
}

.weekly-day-header--today {
  border-bottom: 0.5px solid rgba(255, 255, 255, 0.15);
  padding-bottom: 10px;
}

.dh-top {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.w-day-name {
  font-size: 10px;
  font-weight: 800;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: #1a3060;
}

.w-day-name--today {
  color: rgba(255, 255, 255, 0.55);
}

.w-day-date {
  font-size: 10px;
  color: rgba(26, 48, 96, 0.4);
  font-weight: 500;
}

.w-day-date--today {
  font-size: 20px;
  font-family: "Anton", sans-serif;
  color: #fff;
  letter-spacing: 0.01em;
  line-height: 1;
}

.today-badge {
  display: inline-block;
  background: #f5c800;
  color: #1a3060;
  font-size: 8px;
  font-weight: 900;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  padding: 2px 7px;
  border-radius: 20px;
  margin-top: 4px;
  width: fit-content;
}

.w-petugas-item {
  display: flex;
  align-items: center;
  gap: 7px;
}

.w-avatar {
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: #1a3060;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  border: 1.5px solid transparent;
  transition: all 0.2s;
}

.w-avatar--today {
  width: 30px;
  height: 30px;
  background: rgba(255, 255, 255, 0.15);
  border-color: rgba(255, 255, 255, 0.25);
}

.w-avatar--active {
  background: #f5c800;
  border-color: #f5c800;
  box-shadow: 0 0 0 3px rgba(245, 200, 0, 0.25);
}

.w-avatar--active span {
  color: #1a3060;
}

.w-avatar img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}
.w-avatar span {
  color: white;
  font-size: 10px;
  font-weight: 800;
  line-height: 1;
  display: block;
}

.w-info {
  display: flex;
  flex-direction: column;
  gap: 2px;
  overflow: hidden;
  flex: 1;
}

.w-name {
  font-size: 10px;
  font-weight: 700;
  color: #1a3060;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.w-name--today {
  font-size: 12px;
  color: #fff;
  font-weight: 800;
}

.w-shift {
  display: inline-block;
  font-size: 8px;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  padding: 2px 6px;
  border-radius: 4px;
  width: fit-content;
}

.w-shift.pagi {
  background: #e0f7fa;
  color: #0077a8;
}
.w-shift.siang {
  background: #fff3e0;
  color: #c26000;
}

.w-shift--today.pagi {
  background: rgba(0, 180, 220, 0.18);
  color: #7ee8f8;
}
.w-shift--today.siang {
  background: rgba(249, 115, 22, 0.2);
  color: #fdba74;
}

.w-divider {
  height: 0.5px;
  background: rgba(255, 255, 255, 0.12);
  margin: 2px 0;
}

.w-empty {
  text-align: center;
  color: rgba(26, 48, 96, 0.2);
  font-size: 10px;
  font-style: italic;
}
.w-empty--today {
  color: rgba(255, 255, 255, 0.2);
}
</style>
